import { format } from 'date-fns';
import athenaService from './athenaService';

class AvailabilityService {
  static getAvailabilities() {
    return JSON.parse(localStorage.getItem('availabilities') || '{}');
  }

  static setAvailabilities(availabilities) {
    localStorage.setItem('availabilities', JSON.stringify(availabilities));
  }

  static getRecurringPatterns() {
    return JSON.parse(localStorage.getItem('recurringPatterns') || '[]');
  }

  static setRecurringPatterns(patterns) {
    localStorage.setItem('recurringPatterns', JSON.stringify(patterns));
  }

  static formatDateKey(date) {
    return format(new Date(date), 'EEE MMM dd yyyy');
  }

  static addTimeSlot(date, time) {
    const availabilities = this.getAvailabilities();
    const dateKey = this.formatDateKey(date);
    
    if (!availabilities[dateKey]) {
      availabilities[dateKey] = [];
    }
    
    // Prevent duplicate time slots
    if (!availabilities[dateKey].includes(time)) {
      availabilities[dateKey].push(time);
      availabilities[dateKey].sort(); // Ensure times are sorted
      this.setAvailabilities(availabilities);
    }
  }

  static removeTimeSlot(date, time) {
    const availabilities = this.getAvailabilities();
    const dateKey = this.formatDateKey(date);
    
    if (availabilities[dateKey]) {
      availabilities[dateKey] = availabilities[dateKey].filter(t => t !== time);
      
      // Remove the date if no more time slots
      if (availabilities[dateKey].length === 0) {
        delete availabilities[dateKey];
      }
      
      this.setAvailabilities(availabilities);
    }
  }

  static getTimeSlotsForDate(date) {
    const availabilities = this.getAvailabilities();
    const dateKey = this.formatDateKey(date);
    return availabilities[dateKey] || [];
  }

  static addRecurringPattern(pattern) {
    const patterns = this.getRecurringPatterns();
    patterns.push({
      id: Date.now(),
      ...pattern
    });
    this.setRecurringPatterns(patterns);
  }

  static removeRecurringPattern(patternId) {
    const patterns = this.getRecurringPatterns();
    const updatedPatterns = patterns.filter(pattern => pattern.id !== patternId);
    this.setRecurringPatterns(updatedPatterns);
  }

  static generateAvailabilityFromPatterns(startDate, endDate) {
    const patterns = this.getRecurringPatterns();
    const availabilities = this.getAvailabilities();
    
    // Convert dates to Date objects if they're not already
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    
    // Clear out any existing availability in this range
    for (let currentDate = new Date(start); currentDate <= end; currentDate.setDate(currentDate.getDate() + 1)) {
      const dateKey = this.formatDateKey(currentDate);
      delete availabilities[dateKey];
    }
    
    // Apply patterns to generate new availability
    patterns.forEach(pattern => {
      for (let currentDate = new Date(start); currentDate <= end; currentDate.setDate(currentDate.getDate() + 1)) {
        const day = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
        
        // Check if this day matches the pattern
        if (pattern.days.includes(day)) {
          pattern.times.forEach(time => {
            this.addTimeSlot(new Date(currentDate), time);
          });
        }
      }
    });
    
    return this.getAvailabilities();
  }

  /**
   * Helper method to generate all dates in a range that match specified days of week
   * @param {Date|string} startDate - Start date
   * @param {Date|string} endDate - End date
   * @param {Array<number>} days - Array of days of week (0=Sunday, 1=Monday, etc.)
   * @returns {Array<Date>} - Array of matching dates
   */
  static generateDatesInRange(startDate, endDate, days) {
    // Convert dates to Date objects if they're not already
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    
    const result = [];
    
    // Clone the start date to avoid modifying the original
    const current = new Date(start);
    
    // Iterate through all dates in range
    while (current <= end) {
      const dayOfWeek = current.getDay(); // 0=Sunday, 1=Monday, etc.
      
      // If this day is in our target days array, add it to results
      if (days.includes(dayOfWeek)) {
        result.push(new Date(current));
      }
      
      // Move to next day
      current.setDate(current.getDate() + 1);
    }
    
    return result;
  }

  // New methods that use Athena API
  static async getAvailableSlotsFromAthena(departmentId, appointmentTypeId, providerId, startDate, endDate) {
    try {
      console.log('Getting available slots from Athena with params:', {
        departmentId,
        appointmentTypeId,
        providerId,
        startDate,
        endDate
      });
      
      // Format dates if they're Date objects
      const formattedStartDate = startDate instanceof Date ? format(startDate, 'MM/dd/yyyy') : startDate;
      const formattedEndDate = endDate instanceof Date ? format(endDate, 'MM/dd/yyyy') : endDate;
      
      // Call Athena API - athenaService is already instantiated
      const slots = await athenaService.getOpenAppointmentSlots(
        departmentId,
        appointmentTypeId, 
        formattedStartDate,
        providerId
      );
      
      console.log(`Fetched ${slots.length} available slots from Athena API`);
      
      // Transform the response into our format
      const availabilities = {};
      
      slots.forEach(slot => {
        const dateKey = this.formatDateKey(slot.date);
        if (!availabilities[dateKey]) {
          availabilities[dateKey] = [];
        }
        
        // Add the time if not already in the list
        if (!availabilities[dateKey].includes(slot.starttime)) {
          availabilities[dateKey].push(slot.starttime);
        }
      });
      
      // Sort times for each date
      Object.keys(availabilities).forEach(date => {
        availabilities[date].sort();
      });
      
      return availabilities;
    } catch (error) {
      console.error('Error fetching available slots from Athena:', error);
      // Return empty object if there's an error
      return {};
    }
  }

  // Method to book a slot using Athena API
  static async bookSlot(appointmentId, patientId, appointmentTypeId) {
    try {
      if (!appointmentId || !patientId || !appointmentTypeId) {
        throw new Error('Missing required parameters for booking a slot');
      }
      
      const response = await athenaService.bookAppointment({
        appointmentid: appointmentId,
        patientid: patientId,
        appointmenttypeid: appointmentTypeId
      });
      
      console.log('Successfully booked appointment through Athena API:', response);
      return response;
    } catch (error) {
      console.error('Error booking slot through Athena API:', error);
      throw error;
    }
  }

  /**
   * Create availability slots on the Athena API for a specific date
   * @param {string} departmentId - Department ID
   * @param {string} providerId - Provider ID
   * @param {string} appointmentTypeId - Appointment Type ID
   * @param {Date|string} date - Date for the availability
   * @param {Array} timeSlots - Array of time slots with startTime and endTime
   * @returns {Promise<Array>} - Created availability slots
   */
  static async createAthenaAvailability(departmentId, providerId, appointmentTypeId, date, timeSlots) {
    if (!departmentId || !providerId || !appointmentTypeId || !date || !timeSlots) {
      console.error('Missing required parameters for createAthenaAvailability');
      throw new Error('Missing required parameters for createAthenaAvailability');
    }
    
    try {
      console.log('Creating availability on Athena for date:', date);
      
      // Format date if it's a Date object
      const formattedDate = date instanceof Date ? format(date, 'MM/dd/yyyy') : date;
      
      // Parse time slots to Athena format (only start times as an array)
      const appointmentTimes = timeSlots.map(slot => slot.startTime);
      
      // Create the request data
      const requestData = {
        departmentid: departmentId,
        providerid: providerId,
        appointmenttypeid: appointmentTypeId,
        appointmentdate: formattedDate,
        appointmenttime: appointmentTimes,
        bypassscheduletimingscheck: true,
        showfullresponse: true,
        showallproviders: true,
        showallresources: true
      };
      
      console.log('Creating Athena availability with data:', requestData);
      
      // Call Athena API - athenaService is already instantiated
      const response = await athenaService.createAvailabilitySlots(requestData);
      
      console.log('Athena availability creation response:', response);
      
      // Also save to local storage for backup/redundancy
      const availabilities = this.getAvailabilities();
      
      if (!availabilities[formattedDate]) {
        availabilities[formattedDate] = [];
      }
      
      // Add new slots to local storage (using start times only to match Athena)
      appointmentTimes.forEach(time => {
        if (!availabilities[formattedDate].includes(time)) {
          availabilities[formattedDate].push(time);
        }
      });
      
      this.setAvailabilities(availabilities);
      
      return response;
    } catch (error) {
      console.error('Error creating Athena availability:', error);
      throw error;
    }
  }

  // Create recurring availability on Athena API for a date range
  static async createRecurringAthenaAvailability(departmentId, providerId, appointmentTypeId, pattern, startDate, endDate) {
    try {
      console.log('Creating recurring Athena availability with pattern:', pattern);
      
      if (!departmentId || !providerId || !appointmentTypeId || !pattern || !startDate || !endDate) {
        throw new Error('Missing required parameters for creating recurring availability');
      }
      
      if (!pattern.days || pattern.days.length === 0 || !pattern.times || pattern.times.length === 0) {
        throw new Error('Invalid pattern: must include days and times');
      }
      
      // Format dates
      const formattedStartDate = startDate instanceof Date ? format(startDate, 'MM/dd/yyyy') : startDate;
      const formattedEndDate = endDate instanceof Date ? format(endDate, 'MM/dd/yyyy') : endDate;
      
      console.log(`Creating recurring availability from ${formattedStartDate} to ${formattedEndDate}`);
      
      // Generate all dates in range that match the pattern days
      const dates = this.generateDatesInRange(startDate, endDate, pattern.days);
      console.log(`Generated ${dates.length} dates matching pattern`);
      
      const results = [];
      
      // For each date, create availability for all times in the pattern
      for (const date of dates) {
        const formattedDate = format(date, 'MM/dd/yyyy');
        console.log(`Creating availability for date: ${formattedDate} with ${pattern.times.length} time slots`);
        
        // Create the request data
        const requestData = {
          departmentid: departmentId,
          providerid: providerId,
          appointmenttypeid: appointmentTypeId,
          appointmentdate: formattedDate,
          appointmenttime: pattern.times,
          bypassscheduletimingscheck: true,
          showfullresponse: true
        };
        
        try {
          const response = await athenaService.createAvailabilitySlots(requestData);
          results.push(response);
          
          // Save to local storage for backup/redundancy
          const availabilities = this.getAvailabilities();
          
          if (!availabilities[formattedDate]) {
            availabilities[formattedDate] = [];
          }
          
          // Add new times to local storage
          pattern.times.forEach(time => {
            if (!availabilities[formattedDate].includes(time)) {
              availabilities[formattedDate].push(time);
            }
          });
          
          this.setAvailabilities(availabilities);
        } catch (error) {
          console.error(`Error creating availability for date ${formattedDate}:`, error);
          // Continue with other dates
        }
      }
      
      return results;
    } catch (error) {
      console.error('Error creating recurring Athena availability:', error);
      throw error;
    }
  }
}

export default AvailabilityService;
